import { useState, useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';

export interface NavChild {
  to: string;
  label: string;
  show: boolean;
}

interface Props {
  /** Stable id; drives test ids (`${id}-group/button/dropdown`). */
  id: string;
  /** Group label shown on the button (and as the collapsed-to-one-child link). */
  label: string;
  /** Whether the current route is under this group (active styling). */
  active: boolean;
  /** Children; filtered by `show` here. */
  items: NavChild[];
  /** Shared desktop NavLink class fn (for the collapsed single-child case). */
  navClass: (p: { isActive: boolean }) => string;
}

/**
 * A desktop header nav group: a button that toggles a dropdown of child links.
 * Mirrors the original CXC group, generalized so several groups (Ventas, CXC, …)
 * share one implementation. Self-contained open state + click-outside.
 *   - 0 visible children → renders nothing
 *   - 1 visible child    → plain NavLink (group label → that child)
 *   - 2+ visible children → button + dropdown panel
 */
export default function NavDropdown({ id, label, active, items, navClass }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const visible = items.filter((c) => c.show);

  // Close when clicking outside the group.
  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  if (visible.length === 0) return null;

  if (visible.length === 1) {
    return (
      <NavLink to={visible[0].to} className={navClass}>
        {label}
      </NavLink>
    );
  }

  const btnClass =
    'text-sm px-2 py-1 rounded cursor-pointer ' +
    (active
      ? 'bg-tremor-brand text-tremor-brand-inverted'
      : 'text-tremor-content hover:text-tremor-content-strong');

  return (
    <div ref={ref} className="relative" data-testid={`${id}-group`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={btnClass}
        aria-haspopup="true"
        aria-expanded={open}
        data-testid={`${id}-button`}
      >
        {label}
      </button>
      {open && (
        <div
          className="absolute left-0 top-full mt-1 min-w-max rounded border border-tremor-border bg-tremor-background dark:bg-dark-tremor-background dark:border-dark-tremor-border shadow-md z-dropdown"
          data-testid={`${id}-dropdown`}
        >
          {visible.map((child) => (
            <NavLink
              key={child.to}
              to={child.to}
              className={({ isActive }) =>
                'block px-4 py-2 text-sm whitespace-nowrap ' +
                (isActive
                  ? 'bg-tremor-brand text-tremor-brand-inverted'
                  : 'text-tremor-content hover:text-tremor-content-strong hover:bg-tremor-background-muted dark:hover:bg-dark-tremor-background-muted')
              }
              onClick={() => setOpen(false)}
            >
              {child.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}
